import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Shield, RotateCcw, Save, Loader2 } from 'lucide-react'
import { getEffectivePermissions, updatePermission, grantPermission, revokePermission } from '../../services/api'
import type { EffectivePermissions } from '../../types'

interface CollaboratorPermissionsModalProps {
  isOpen: boolean
  onClose: () => void
  resourceId: string
  resourceName: string
  /** The type of resource resourceId refers to. BUG-027: previously hardcoded
   * to 'folder', which mislabelled project-scope grants created from
   * ProjectView. Each caller must pass the type matching its resourceId. */
  resourceType: 'project' | 'folder' | 'document'
  granteeId: string
  granteeEmail: string
  isManager: boolean
  onUpdated?: () => void
}

const CAPABILITY_LABELS: { field: keyof Pick<EffectivePermissions, 'can_create' | 'can_read' | 'can_edit' | 'can_delete' | 'can_manage_collaborators'>; label: string }[] = [
  { field: 'can_create', label: 'Create documents' },
  { field: 'can_read', label: 'Read documents' },
  { field: 'can_edit', label: 'Edit documents' },
  { field: 'can_delete', label: 'Delete documents' },
  { field: 'can_manage_collaborators', label: 'Manage collaborators' },
]

export default function CollaboratorPermissionsModal({
  isOpen,
  onClose,
  resourceId,
  resourceName,
  resourceType,
  granteeId,
  granteeEmail,
  isManager,
  onUpdated,
}: CollaboratorPermissionsModalProps) {
  const [loading, setLoading] = useState(true)
  const [effective, setEffective] = useState<EffectivePermissions | null>(null)
  const [editedCaps, setEditedCaps] = useState<Record<string, boolean>>({})
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  useEffect(() => {
    if (!isOpen || !granteeId) return
    setLoading(true)
    setError('')
    getEffectivePermissions(resourceId, granteeId)
      .then((perms) => {
        setEffective(perms)
        setEditedCaps({
          can_create: perms.can_create,
          can_read: perms.can_read,
          can_edit: perms.can_edit,
          can_delete: perms.can_delete,
          can_manage_collaborators: perms.can_manage_collaborators,
        })
      })
      .catch(() => setError('Failed to load permissions'))
      .finally(() => setLoading(false))
  }, [isOpen, resourceId, granteeId])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!effective) return
      if (effective.source === 'explicit') {
        await updatePermission(resourceId, granteeId, {
          permission_level: effective.permission_level || 'view',
          ...editedCaps,
        })
      } else {
        // Create explicit permission on this resource
        await grantPermission({
          resource_id: resourceId,
          grantee_email: granteeEmail,
          permission_level: effective.permission_level || 'view',
          resource_type: resourceType,
          ...editedCaps,
        })
      }
    },
    onSuccess: () => {
      setSuccessMsg('Permissions updated')
      onUpdated?.()
      setTimeout(() => setSuccessMsg(''), 2000)
      // Refresh effective permissions
      getEffectivePermissions(resourceId, granteeId).then(setEffective)
    },
    onError: () => setError('Failed to update permissions'),
  })

  const resetMutation = useMutation({
    mutationFn: () => revokePermission(resourceId, granteeId),
    onSuccess: () => {
      setSuccessMsg('Override removed — using inherited permissions')
      onUpdated?.()
      setTimeout(() => {
        setSuccessMsg('')
        onClose()
      }, 1500)
    },
    onError: () => setError('Failed to reset permissions'),
  })

  const toggleCap = (field: string) => {
    setEditedCaps((prev) => ({ ...prev, [field]: !prev[field] }))
  }

  if (!isOpen) return null

  const sourceLabel = effective?.source === 'owner'
    ? 'Project owner (implicit)'
    : effective?.source === 'inherited'
      ? `Inherited from ${effective.inherited_from || 'parent'}`
      : effective?.source === 'explicit'
        ? 'Explicitly set on this resource'
        : 'No permissions'

  const isExplicit = effective?.source === 'explicit'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield size={18} className="text-iflow-400" />
            Permissions
          </h3>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white/60">
            <X size={18} />
          </button>
        </div>

        {/* Collaborator info */}
        <div className="mb-4 p-3 bg-white/5 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-iflow-500/20 text-iflow-300 flex items-center justify-center text-sm font-medium">
              {granteeEmail.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-medium text-white">{granteeEmail}</div>
              <div className="text-xs text-white/60">{resourceName}</div>
            </div>
          </div>
        </div>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-400/30 rounded-lg text-sm text-green-300">
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-lg text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-iflow-500" />
          </div>
        ) : effective ? (
          <div className="space-y-4">
            {/* Permission source */}
            <div>
              <div className="text-xs font-medium text-white/60 uppercase mb-1">Source</div>
              <div className="text-sm text-white/80">{sourceLabel}</div>
              {effective.permission_level && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-iflow-500/20 text-iflow-300 rounded">
                  {effective.permission_level}
                </span>
              )}
            </div>

            {/* Granular permissions */}
            <div>
              <div className="text-xs font-medium text-white/60 uppercase mb-2">Capabilities</div>
              <div className="space-y-2">
                {CAPABILITY_LABELS.map(({ field, label }) => (
                  <label
                    key={field}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                      isManager && effective.source !== 'owner'
                        ? 'border-white/20 hover:bg-white/5 cursor-pointer'
                        : 'border-white/10 bg-white/5'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={editedCaps[field] ?? false}
                      onChange={() => toggleCap(field)}
                      disabled={!isManager || effective.source === 'owner'}
                      className="w-4 h-4 text-iflow-500 border-white/30 rounded focus:ring-iflow-500 bg-white/10 disabled:opacity-50"
                    />
                    <span className="text-sm text-white/80 flex-1">{label}</span>
                    {effective.source === 'owner' && (
                      <span className="text-xs text-white/40">owner</span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Action buttons — manager only */}
            {isManager && effective.source !== 'owner' && (
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                {isExplicit && (
                  <button
                    onClick={() => resetMutation.mutate()}
                    disabled={resetMutation.isPending}
                    title="Remove the permissions set specifically on this resource. Access falls back to whatever is inherited from the parent."
                    className="flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 font-medium disabled:opacity-50"
                  >
                    <RotateCcw size={12} />
                    {resetMutation.isPending ? 'Removing…' : 'Remove override'}
                  </button>
                )}
                {!isExplicit && <div />}
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                >
                  <Save size={14} />
                  {saveMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-white/60 py-4">No permission data available.</p>
        )}
      </div>
    </div>
  )
}
